<template>
  <div class="space-y-6">
    <p class="text-sm text-body">{{ t(`onboarding.wizard.sections.${moduleKey}.subtitle`) }}</p>

    <!--
      ADR 0032: sections STAGE their answers; nothing is written to a module until
      Finish. Only fields with a clean 1:1 module endpoint are rendered + staged.
      List fields add entries on top of the module's seeded defaults; the vocab
      defaults (salutations, contract/work-time/location types) stay seeded.
    -->
    <div v-if="fields.length" class="space-y-5">
      <Field
        v-for="f in fields"
        :key="f.key"
        :data-test="`wizard-field-${f.key}`"
        :label="t(`onboarding.wizard.sections.${moduleKey}.fields.${f.key}.label`)"
        :hint="t(`onboarding.wizard.sections.${moduleKey}.fields.${f.key}.hint`)"
      >
        <WizardListInput
          v-if="f.kind === 'list'"
          :model-value="lists[f.key] ?? []"
          :placeholder="t(`onboarding.wizard.sections.${moduleKey}.fields.${f.key}.placeholder`)"
          @update:model-value="(v) => (lists[f.key] = v)"
        />
        <Select
          v-else-if="f.kind === 'select'"
          v-model="values[f.key]"
          :options="selectOptions(f)"
        />
        <Textarea v-else-if="f.kind === 'textarea'" v-model="values[f.key]" :rows="3" />
        <Input v-else v-model="values[f.key]" />
      </Field>
    </div>

    <!-- Timecard-only: the days the company itself closes (Betriebsferien) —
         half of Christmas Eve, the week between the years. Asked at setup
         because they apply from day one: nobody owes hours on them, and each
         either costs everybody a day of leave or is granted on top. Staged like
         every other answer; Finish writes them into Timecard. -->
    <section v-if="moduleKey === 'time_tracking'" class="space-y-4 border-t border-default pt-6">
      <div>
        <h3 class="text-base font-semibold text-heading">{{ t('onboarding.wizard.sections.time_tracking.companyHolidays.title') }}</h3>
        <p class="text-sm text-body">{{ t('onboarding.wizard.sections.time_tracking.companyHolidays.hint') }}</p>
      </div>
      <WizardCompanyHolidaysInput ref="companyHolidaysInput" v-model="companyHolidays" />
    </section>

    <!-- ATS-only: optional "job posting" settings (ADR 0032). Candidate contact +
         job-ad logo, replayed into ats-api's job-posting-settings. All optional
         (the Raven readiness guard enforces them at publish time). -->
    <section v-if="moduleKey === 'ats'" class="space-y-4 border-t border-default pt-6">
      <div>
        <h3 class="text-base font-semibold text-heading">{{ t('onboarding.wizard.sections.ats.jobPosting.title') }}</h3>
        <p class="text-sm text-body">{{ t('onboarding.wizard.sections.ats.jobPosting.hint') }}</p>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field :label="t('onboarding.wizard.sections.ats.jobPosting.contactName')">
          <Input v-model="values.contactName" />
        </Field>
        <Field :label="t('onboarding.wizard.sections.ats.jobPosting.contactEmail')">
          <Input v-model="values.contactEmail" type="email" :invalid="emailInvalid" />
          <p v-if="emailInvalid" class="mt-1 text-xs text-danger-text">
            {{ t('onboarding.wizard.sections.ats.jobPosting.contactEmailInvalid') }}
          </p>
        </Field>
      </div>
      <Field :label="t('onboarding.wizard.sections.ats.jobPosting.logo')">
        <div class="flex items-center gap-3">
          <input ref="logoInput" type="file" accept="image/*" class="sr-only" @change="onLogoSelected" />
          <Button variant="secondary" @click="pickLogo">{{ t('onboarding.wizard.sections.ats.jobPosting.uploadLogo') }}</Button>
          <span v-if="logoFile" class="text-sm text-body truncate">{{ logoFile.name }}</span>
          <Button v-if="logoFile" variant="link" @click="removeLogo">{{ t('onboarding.wizard.sections.ats.jobPosting.removeLogo') }}</Button>
        </div>
        <p v-if="!logoFile && logoWasPicked" class="mt-1 text-xs text-warning-text">
          {{ t('onboarding.wizard.sections.ats.jobPosting.rePick') }}
        </p>
      </Field>
    </section>

    <!-- ATS-only: two plain questions the hiring-chain generator branches on (issue
         23) — who is involved in job ads/applications, and whether the tenant runs
         assessment centres. Staged like every other section — the chain is
         generated once, at Finish, from these answers. Leaving them untouched IS
         the lean default, so skipping the section costs nothing. -->
    <section v-if="moduleKey === 'ats'" class="space-y-4 border-t border-default pt-6">
      <div>
        <h3 class="text-base font-semibold text-heading">{{ t('onboarding.wizard.sections.ats.workflow.title') }}</h3>
      </div>
      <!-- One answer per line (issue 1136): the checkboxes are inline-flex, so
           short labels would otherwise share a row and wrap unevenly. -->
      <div class="flex flex-col items-start gap-2">
        <p class="text-sm font-medium text-heading">{{ t('onboarding.wizard.sections.ats.workflow.participantsQuestion') }}</p>
        <Checkbox
          v-model="workflow.worksCouncil"
          data-test="wizard-works-council"
          :label="t('onboarding.wizard.sections.ats.workflow.worksCouncil')"
        />
        <Checkbox
          v-model="workflow.sbv"
          data-test="wizard-sbv"
          :label="t('onboarding.wizard.sections.ats.workflow.sbv')"
        />
        <Checkbox
          v-model="workflow.legalSignOff"
          data-test="wizard-legal-sign-off"
          :label="t('onboarding.wizard.sections.ats.workflow.legalSignOff')"
        />
      </div>
      <div class="flex flex-col items-start gap-2">
        <p class="text-sm font-medium text-heading">{{ t('onboarding.wizard.sections.ats.workflow.assessmentCentreQuestion') }}</p>
        <Checkbox
          v-model="assessmentCentre"
          data-test="wizard-assessment-centre"
          :label="t('onboarding.wizard.sections.ats.workflow.assessmentCentre')"
        />
      </div>
      <p class="text-xs text-body">{{ t('onboarding.wizard.sections.ats.workflow.hint') }}</p>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Checkbox, Field, Input, Select, Textarea } from '@lumi/ui';
import { useApiMutation } from '@lumi/app-kit';
import { userApi } from '../../utils/api';
import { useOnboardingStore, type OnboardingStatus } from '../../stores/onboarding';
import WizardListInput from './WizardListInput.vue';
import WizardCompanyHolidaysInput from './WizardCompanyHolidaysInput.vue';
import { normalizeCompanyHolidays, type StagedCompanyHoliday } from '../../utils/companyHolidays';

const props = defineProps<{ moduleKey: 'ats' | 'time_tracking' | 'apprenticeship' | 'people' }>();

const { t } = useI18n();
const onboardingStore = useOnboardingStore();

type FieldDef = { key: string; kind: 'text' | 'textarea' | 'list' | 'select'; options?: string[] };

// Only fields with a clean 1:1 module endpoint (replayed at Finish). List fields
// stage a string[]; select fields stage a string; text/textarea stage a string.
const FIELD_SETS: Record<string, FieldDef[]> = {
  ats: [
    { key: 'fromName', kind: 'text' },
    { key: 'signature', kind: 'textarea' },
  ],
  time_tracking: [{ key: 'bookingTypes', kind: 'list' }],
  apprenticeship: [
    { key: 'professions', kind: 'list' },
  ],
  people: [
    { key: 'salutations', kind: 'list' },
    { key: 'contractTypes', kind: 'list' },
    { key: 'workTimeModels', kind: 'list' },
    { key: 'workLocationTypes', kind: 'list' },
  ],
};

const fields = computed<FieldDef[]>(() => FIELD_SETS[props.moduleKey] ?? []);
const values = reactive<Record<string, string>>({});
const lists = reactive<Record<string, string[]>>({});

function selectOptions(f: FieldDef) {
  return (f.options ?? []).map((value) => ({
    value,
    label: t(`onboarding.wizard.sections.${props.moduleKey}.fields.${f.key}.options.${value}`),
  }));
}

// The hiring-chain answers (issue 23): three participant checkboxes plus whether
// the tenant runs assessment centres. These four staged fields are the
// generator's whole input; the defaults below ARE the lean default every tenant
// is seeded with, so an admin who skips this section gets exactly what they get
// today. Nothing is generated here — the answers are staged and replayed at
// Finish like every other section.
const INTERVIEW_SHAPES = ['interviews_only', 'assessment_centre'] as const;
type InterviewShape = (typeof INTERVIEW_SHAPES)[number];
const workflow = reactive<{
  worksCouncil: boolean;
  sbv: boolean;
  legalSignOff: boolean;
  interviewShape: InterviewShape;
}>({ worksCouncil: false, sbv: false, legalSignOff: false, interviewShape: 'interviews_only' });

// The assessment-centre question is a single checkbox; `workflow.interviewShape`
// stays the staged/replayed value (unticked → 'interviews_only', ticked →
// 'assessment_centre') so the contract to ats-api's generator does not change.
const assessmentCentre = computed<boolean>({
  get: () => workflow.interviewShape === 'assessment_centre',
  set: (value: boolean) => {
    workflow.interviewShape = value ? 'assessment_centre' : 'interviews_only';
  },
});

// ATS candidate-contact e-mail: a non-empty value must be a valid address (an empty
// one clears the field). Mirrors JobPostingSettingsView's check. Validated at stage
// time because ats-api's job-posting-settings DTO rejects an invalid e-mail at
// replay — by which point the router has pinned the user to the commit screen with
// no path back to fix the staged value.
const emailInvalid = computed(() => {
  const v = (values.contactEmail || '').trim();
  return v.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
});

// The job-ad logo File is held client-side only (ADR 0032). `logoWasPicked` is
// staged; after a reload the File is gone but the flag survives → we show
// "re-pick" instead of pretending it's saved.
// Timecard's company holidays (Betriebsferien): a list of their own, because an
// entry is more than a label — see WizardCompanyHolidaysInput.
const companyHolidays = ref<StagedCompanyHoliday[]>([]);
const companyHolidaysInput = ref<{ flush: () => void } | null>(null);

const logoInput = ref<HTMLInputElement | null>(null);
const logoWasPicked = ref(false);
const logoFile = computed(() => onboardingStore.stagedLogoFile);

onMounted(() => {
  // Resume from the staged blob — nothing is read from a module API (staged model).
  const staged = onboardingStore.staged[props.moduleKey] || {};
  for (const f of fields.value) {
    const v = (staged as any)[f.key];
    if (f.kind === 'list') lists[f.key] = Array.isArray(v) ? v.map((x) => String(x)) : [];
    else if (typeof v === 'string') values[f.key] = v;
  }
  if (props.moduleKey === 'time_tracking') companyHolidays.value = normalizeCompanyHolidays((staged as any).companyHolidays);
  if (props.moduleKey === 'ats') {
    if (typeof (staged as any).contactName === 'string') values.contactName = (staged as any).contactName;
    if (typeof (staged as any).contactEmail === 'string') values.contactEmail = (staged as any).contactEmail;
    logoWasPicked.value = !!(staged as any).logoPicked;
    const stagedWorkflow = (staged as any).workflow;
    if (stagedWorkflow && typeof stagedWorkflow === 'object') {
      workflow.worksCouncil = stagedWorkflow.worksCouncil === true;
      workflow.sbv = stagedWorkflow.sbv === true;
      workflow.legalSignOff = stagedWorkflow.legalSignOff === true;
      workflow.interviewShape = INTERVIEW_SHAPES.includes(stagedWorkflow.interviewShape)
        ? stagedWorkflow.interviewShape
        : 'interviews_only';
    }
  }
});

function pickLogo() {
  logoInput.value?.click();
}
function onLogoSelected(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0] || null;
  if (file) {
    onboardingStore.stagedLogoFile = file;
    logoWasPicked.value = true;
  }
  if (logoInput.value) logoInput.value.value = '';
}
function removeLogo() {
  onboardingStore.stagedLogoFile = null;
  logoWasPicked.value = false;
}

// inlineEdit + the default `global` channel: a successful save is confirmed by
// the wizard advancing to the next section, and a failure has nowhere in this
// section to render — so it takes the channel that cannot be silent, and
// `persistComplete` returns false to keep the user on the section they were on.
const { mutate: stageAnswers } = useApiMutation<void, [string, Record<string, any>]>({
  tier: 'inlineEdit',
  run: async (key, payload) => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    const res = await userApi.put(
      `${onboardingStore.base(tenantId)}/staged/${encodeURIComponent(key)}`,
      payload,
    );
    onboardingStore.apply(res.data as OnboardingStatus);
  },
});

// Stage the section (ADR 0032): no module write. The replay at Finish applies it.
// Returns false to block advancing/committing when a field is invalid (the ATS
// contact e-mail is the only gate; everything else is optional).
async function persistComplete(): Promise<boolean> {
  const payload: Record<string, any> = {};
  for (const f of fields.value) {
    if (f.kind === 'list') payload[f.key] = lists[f.key] ?? [];
    else if (values[f.key] !== undefined) payload[f.key] = values[f.key];
  }
  if (props.moduleKey === 'time_tracking') {
    // An entry filled in but never added with ➕ is kept, not dropped (#546).
    companyHolidaysInput.value?.flush();
    await nextTick();
    payload.companyHolidays = companyHolidays.value;
  }
  if (props.moduleKey === 'ats') {
    if (emailInvalid.value) return false; // the field shows the error; stay on the section
    payload.contactName = values.contactName ?? '';
    payload.contactEmail = values.contactEmail ?? '';
    payload.logoPicked = logoWasPicked.value;
    // Staged as its own object so the replay reads one contract, not four loose keys.
    payload.workflow = {
      worksCouncil: workflow.worksCouncil,
      sbv: workflow.sbv,
      legalSignOff: workflow.legalSignOff,
      interviewShape: workflow.interviewShape,
    };
  }
  const outcome = await stageAnswers(props.moduleKey, payload);
  return outcome.ok;
}

defineExpose({ persistComplete });
</script>
